/**
 * Gráfico Circular de Estado de Candidatos
 * Muestra la distribución de candidatos por estado (evaluados, en proceso, descartados)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Card from '@mui/material/Card';

// Registrar componentes de Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

const CandidatesPieChart = ({ data, title = "Estado de Candidatos" }) => {
  // Si no hay datos, mostrar mensaje
  if (!data || data.total === 0) {
    return (
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h6" fontWeight="medium">
            {title}
          </MDTypography>
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="body2" color="text">
              Sin registros disponibles
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  // Configuración de datos del gráfico
  const chartData = {
    labels: ['Evaluados', 'En Proceso', 'Descartados'],
    datasets: [
      {
        label: 'Candidatos',
        data: [data.evaluated || 0, data.inProgress || 0, data.discarded || 0],
        backgroundColor: [
          'rgba(76, 175, 80, 0.8)',   // Verde - Evaluados
          'rgba(33, 150, 243, 0.8)',  // Azul - En Proceso
          'rgba(244, 67, 54, 0.8)',   // Rojo - Descartados
        ],
        borderColor: [
          'rgba(76, 175, 80, 1)',
          'rgba(33, 150, 243, 1)',
          'rgba(244, 67, 54, 1)',
        ],
        borderWidth: 2,
        hoverOffset: 4,
      },
    ],
  };

  // Opciones del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12,
            family: 'Roboto, sans-serif',
          },
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = ((value / data.total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h6" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        
        {/* Estadísticas rápidas */}
        <MDBox mb={3} display="flex" justifyContent="space-around">
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="success">
              {data.evaluated}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Evaluados
            </MDTypography>
          </MDBox>
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="info">
              {data.inProgress}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              En Proceso
            </MDTypography>
          </MDBox>
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="error">
              {data.discarded}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Descartados
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Gráfico circular */}
        <MDBox sx={{ maxWidth: '400px', margin: '0 auto' }}>
          <Pie data={chartData} options={options} />
        </MDBox>

        {/* Total de candidatos */}
        <MDBox mt={2} textAlign="center">
          <MDTypography variant="body2" color="text">
            Total de candidatos: <strong>{data.total}</strong>
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
};

CandidatesPieChart.propTypes = {
  data: PropTypes.shape({
    total: PropTypes.number.isRequired,
    evaluated: PropTypes.number.isRequired,
    inProgress: PropTypes.number.isRequired,
    discarded: PropTypes.number.isRequired,
    percentages: PropTypes.shape({
      evaluated: PropTypes.number,
      inProgress: PropTypes.number,
      discarded: PropTypes.number,
    }),
  }).isRequired,
  title: PropTypes.string,
};

export default CandidatesPieChart;
